import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, BookOpen, ShieldCheck } from "lucide-react";

/**
 * Shared layout for /docs/* manual pages.
 * Children render the manual sections; the layout supplies the top bar,
 * sticky table of contents, and footer back-link.
 */
export default function DocsLayout({ title, eyebrow, lastUpdated, sections, children }) {
  const location = useLocation();
  const isUser = location.pathname.includes("user-manual");

  return (
    <div className="min-h-screen" style={{ background: "var(--ll-bg)" }}>
      {/* Top bar */}
      <header className="border-b sticky top-0 z-20 backdrop-blur" style={{ borderColor: "var(--ll-border)", background: "rgba(249,249,246,0.9)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="docs-logo">
            <div className="w-7 h-7 grid place-items-center bg-[var(--ll-ink)] text-white"><span className="font-display font-bold text-sm">F</span></div>
            <span className="font-display font-bold text-lg">Financials Ready</span>
            <span className="ml-2 text-xs uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">/ Docs</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <Link
              to="/docs/user-manual"
              data-testid="docs-nav-user"
              className={`px-3 py-2 flex items-center gap-1.5 transition-colors ${isUser ? "bg-[var(--ll-ink)] text-white" : "hover:bg-[var(--ll-mint)]"}`}
            >
              <BookOpen size={14} /> User manual
            </Link>
            <Link
              to="/docs/admin-manual"
              data-testid="docs-nav-admin"
              className={`px-3 py-2 flex items-center gap-1.5 transition-colors ${!isUser ? "bg-[var(--ll-ink)] text-white" : "hover:bg-[var(--ll-mint)]"}`}
            >
              <ShieldCheck size={14} /> Admin manual
            </Link>
          </nav>
          <Link to="/" data-testid="docs-back-home" className="text-sm px-4 py-2 hover:bg-[var(--ll-mint)] flex items-center gap-1.5">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b" style={{ borderColor: "var(--ll-border)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-3">{eyebrow}</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-none mb-4">{title}</h1>
          {lastUpdated && (
            <div className="text-xs uppercase tracking-widest text-[var(--ll-ink-muted)]">Last updated · {lastUpdated}</div>
          )}
        </div>
      </section>

      {/* Body */}
      <section>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 grid lg:grid-cols-12 gap-10">
          {/* TOC */}
          <aside className="lg:col-span-3">
            <div className="sticky top-24">
              <div className="text-xs uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-3">On this page</div>
              <ul className="space-y-1.5 text-sm">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      data-testid={`docs-toc-${s.id}`}
                      className="hover:text-[var(--ll-ink)] text-[var(--ll-ink-soft)] hover:underline underline-offset-4"
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Content */}
          <article className="lg:col-span-9 space-y-12" data-testid="docs-article">
            {children}
          </article>
        </div>
      </section>

      <footer className="border-t" style={{ borderColor: "var(--ll-border)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-wrap items-center justify-between gap-3 text-xs text-[var(--ll-ink-muted)]">
          <div>© {new Date().getFullYear()} Financials Ready — Quiet precision for modern books.</div>
          <div className="flex items-center gap-6">
            <Link to="/docs/user-manual" className="hover:text-[var(--ll-ink)]">User manual</Link>
            <Link to="/docs/admin-manual" className="hover:text-[var(--ll-ink)]">Admin manual</Link>
            <Link to="/" className="hover:text-[var(--ll-ink)]">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function DocSection({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`docs-section-${id}`}>
      <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-4">{title}</h2>
      <div className="prose-ll text-[var(--ll-ink-soft)] leading-relaxed space-y-4 text-[15px]">
        {children}
      </div>
    </section>
  );
}

export function Steps({ items }) {
  return (
    <ol className="space-y-4 mt-2">
      {items.map((it, i) => (
        <li key={i} className="grid grid-cols-[auto,1fr] gap-4 items-start">
          <span className="font-mono-fin text-xs w-7 h-7 grid place-items-center border" style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg-card)" }}>
            {String(i + 1).padStart(2, "0")}
          </span>
          <div>
            <div className="font-medium text-[var(--ll-ink)]">{it.title}</div>
            {it.body && <div className="text-sm text-[var(--ll-ink-soft)] mt-1">{it.body}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

export function Callout({ kind = "tip", title, children }) {
  const palette = {
    tip: { bg: "#ECFDF5", border: "#10B981", text: "#065F46" },
    warn: { bg: "#FEF2F2", border: "#F87171", text: "#991B1B" },
    note: { bg: "#FEFCE8", border: "#EAB308", text: "#854D0E" },
  }[kind] || { bg: "#EFF6FF", border: "#1B4332", text: "#1E40AF" };
  return (
    <div
      className="border-l-4 p-4 my-2"
      style={{ background: palette.bg, borderColor: palette.border, color: palette.text }}
    >
      <div className="text-[10px] uppercase tracking-[0.2em] font-medium mb-1">{title || kind}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}
